import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Collapse,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import {
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PrintIcon from '@mui/icons-material/Print';
import { useSnackbar } from 'notistack';
import cuentaCorrienteProveedoresService, {
  CuentaCorrienteProveedorComprobante,
  CuentaCorrienteProveedorSaldo,
  MovimientoHistoricoProveedor
} from '../../services/cuentaCorrienteProveedoresService';
import proveedorService from '../../services/proveedorService';
import { formatCurrency } from '../../utils/formatters';
import * as XLSX from 'xlsx';
import ProveedorSearch from '../../components/ProveedorSearch';
import ProveedorInlineSearchField from '../../components/ProveedorInlineSearchField';
import AplicarSaldoReciboDialog from '../../components/Tesoreria/AplicarSaldoReciboDialog';
import PDFViewer from '../../components/shared/PDFViewer';
import api from '../../services/api';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { esCreditoPagoProveedor, esDebitoPagoProveedor, esNotaCreditoProveedor } from '../../constants/tiposComprobante';
import type { CreditoConSaldoPendiente } from '../../services/tesoreriaAplicacionesService';

const formatterFecha = new Intl.DateTimeFormat('es-AR', {
  dateStyle: 'short'
});

/** Formatea yyyy-MM-dd en hora local (evita corrimiento -1 día de new Date('yyyy-MM-dd') = UTC). */
const formatFechaSoloDia = (fecha: string) =>
  formatterFecha.format(new Date(`${fecha}T12:00:00`));

const LABEL_SALDO_CUENTA_CORRIENTE_PERIODO = 'Saldo de cuenta corriente del período';

/** Quita el prefijo redundante (FACTURA, RECIBO, ORDEN DE PAGO, NOTA DE CRÉDITO, etc.) del número de comprobante */
const numeroSinPrefijo = (numero: string | null | undefined): string => {
  if (!numero || !numero.trim()) return '-';
  const s = numero.trim();
  const match = s.match(/^(FACTURA|RECIBO|ORDEN\s+DE\s+PAGO|NOTA\s+DE\s+CR[EÉeé]D[IÍií]TO|NOTA\s+DE\s+D[EÉeé]B[IÍií]TO|DEVOLUCI[OÓoó]N|NCI|NDI|FC|ND|NC|REC|DEV)-/i);
  if (match) return s.slice(match[0].length).trim() || '-';
  return s || '-';
};

const creditoDesdeResumenItem = (
  nroFormulario: number | null | undefined,
  nroTipo: string | null | undefined,
  comprobanteTxt: string,
  numero: string
): CreditoConSaldoPendiente | null => {
  const nroForm = nroFormulario != null && Number(nroFormulario) > 0 ? Number(nroFormulario) : 0;
  if (!nroForm) return null;
  const texto = `${comprobanteTxt} ${numero}`.trim();
  if (!esCreditoPagoProveedor(nroTipo ?? '', texto)) return null;
  const tipo = esNotaCreditoProveedor(nroTipo ?? '', texto) ? '3' : '6';
  const nro = numeroSinPrefijo(numero);
  const m = nro.match(/([A-Za-z])-(\d+)-(\d+)/);
  return {
    nroFormulario: nroForm,
    nroTipoFormulario: tipo,
    letra: m?.[1]?.toUpperCase(),
    nroSuc: m ? Number(m[2]) : 0,
    nroComprobante: m ? Number(m[3]) : undefined,
    totalAplicado: 0,
    saldoPendiente: 0,
  };
};

type ResumenCuentaNavState = {
  from?: string;
  fromLabel?: string;
};

const INFORMES_COMPRAS_PATH = '/compras/informes';
const CUENTAS_PAGAR_PATH = '/compras/informes/cuentas-pagar';

const ResumenCuenta: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { nroProveedor } = useParams<{ nroProveedor: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navState = (location.state as ResumenCuentaNavState | null) ?? null;
  const backTo =
    navState?.from === CUENTAS_PAGAR_PATH ? CUENTAS_PAGAR_PATH : INFORMES_COMPRAS_PATH;
  const backLabel =
    navState?.from === CUENTAS_PAGAR_PATH
      ? navState.fromLabel?.trim() || 'Cuentas por Pagar'
      : 'Informes';
  
  const [allComprobantes, setAllComprobantes] = useState<CuentaCorrienteProveedorComprobante[]>([]);
  const [proveedorInfo, setProveedorInfo] = useState<CuentaCorrienteProveedorSaldo | null>(null);
  const [openProveedorSearch, setOpenProveedorSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentTab, setCurrentTab] = useState(0);
  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');
  const [searchTermMovimientos, setSearchTermMovimientos] = useState<string>('');
  const [pageMovimientos, setPageMovimientos] = useState(0);
  const [rowsPerPageMovimientos, setRowsPerPageMovimientos] = useState(10);
  const [movimientosHistoricos, setMovimientosHistoricos] = useState<MovimientoHistoricoProveedor[]>([]);
  const [totalCountMovimientos, setTotalCountMovimientos] = useState(0);
  const [loadingMovimientos, setLoadingMovimientos] = useState(false);
  const [saldoInicialPaginaMovimientos, setSaldoInicialPaginaMovimientos] = useState(0);
  const [saldoInicialPeriodo, setSaldoInicialPeriodo] = useState<number | null>(null);
  const [exportingExcelMovimientos, setExportingExcelMovimientos] = useState(false);
  const [exportingPDFMovimientos, setExportingPDFMovimientos] = useState(false);
  const [showFiltrosMovimientos, setShowFiltrosMovimientos] = useState(false);
  const [searchTermComposicion, setSearchTermComposicion] = useState<string>('');
  const [showFiltrosComposicion, setShowFiltrosComposicion] = useState(false);
  const [openAplicarSaldoDialog, setOpenAplicarSaldoDialog] = useState(false);
  const [nroFormularioCreditoAplicar, setNroFormularioCreditoAplicar] = useState<number | null>(null);
  const [debitoIdVerAplicaciones, setDebitoIdVerAplicaciones] = useState<number | null>(null);
  const [debitoTipoVerAplicaciones, setDebitoTipoVerAplicaciones] = useState<string | undefined>(undefined);
  const [numeroComprobanteCreditoAplicar, setNumeroComprobanteCreditoAplicar] = useState<string | undefined>(
    undefined
  );
  const [numeroComprobanteDebitoAplicar, setNumeroComprobanteDebitoAplicar] = useState<string | undefined>(
    undefined
  );
  const [creditoTipoAplicar, setCreditoTipoAplicar] = useState<string | undefined>(undefined);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfOrdenPagoId, setPdfOrdenPagoId] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);

  const creditosConocidosResumen = useMemo(() => {
    const map = new Map<number, CreditoConSaldoPendiente>();
    const add = (cred: CreditoConSaldoPendiente | null) => {
      if (cred && !map.has(cred.nroFormulario)) map.set(cred.nroFormulario, cred);
    };
    for (const item of allComprobantes) {
      add(
        creditoDesdeResumenItem(
          item.nroFormulario,
          item.nroTipoFormulario,
          item.comprobante ?? '',
          item.numero ?? ''
        )
      );
    }
    for (const item of movimientosHistoricos) {
      add(
        creditoDesdeResumenItem(
          item.nroFormulario,
          item.nroTipoFormulario,
          `${item.descripcionTip ?? ''} ${item.descripcionFor ?? ''}`,
          item.numero ?? ''
        )
      );
    }
    return Array.from(map.values());
  }, [allComprobantes, movimientosHistoricos]);

  const handleCloseAplicarSaldoDialog = () => {
    setOpenAplicarSaldoDialog(false);
    setNroFormularioCreditoAplicar(null);
    setDebitoIdVerAplicaciones(null);
    setDebitoTipoVerAplicaciones(undefined);
    setNumeroComprobanteCreditoAplicar(undefined);
    setNumeroComprobanteDebitoAplicar(undefined);
    setCreditoTipoAplicar(undefined);
  };

  const abrirVistaDebitoAplicaciones = (
    nroForm: number,
    nroTipo: string,
    comprobanteTxt = '',
    numeroComprobante = ''
  ) => {
    setDebitoIdVerAplicaciones(nroForm);
    setDebitoTipoVerAplicaciones(nroTipo || undefined);
    setNroFormularioCreditoAplicar(null);
    setNumeroComprobanteCreditoAplicar(undefined);
    setCreditoTipoAplicar(undefined);
    const nro = numeroComprobante.trim();
    setNumeroComprobanteDebitoAplicar(nro && nro !== '-' ? nro : undefined);
    setOpenAplicarSaldoDialog(true);
  };

  const handleImprimirPDFOrdenPago = async (nroFormulario: number) => {
    try {
      const response = await api.get(`/api/Tesoreria/ordendepago/${nroFormulario}/pdf`, {
        responseType: 'blob',
      });
      const cd = (response.headers as { 'content-disposition'?: string })?.['content-disposition'];
      setPdfFileName(getFileNameFromContentDisposition(cd));
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl(url);
      setPdfOrdenPagoId(nroFormulario);
    } catch {
      enqueueSnackbar('Error al generar el PDF de la orden de pago', { variant: 'error' });
    }
  };

  const handleClosePDFOrdenPago = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfOrdenPagoId(null);
    setPdfFileName(null);
  };

  const getComprobantePathFromTipo = (
    nroFormulario: number | null | undefined,
    nroTipo: string,
    comprobanteTxt: string,
    numeroTxt = ''
  ): string | null => {
    const nroForm = nroFormulario != null && Number(nroFormulario) > 0 ? Number(nroFormulario) : null;
    if (!nroForm) return null;

    const textoDocumento = `${comprobanteTxt} ${numeroTxt}`.trim();
    if (esDebitoPagoProveedor(nroTipo, textoDocumento)) {
      return `/compras/facturas/${nroForm}`;
    }

    if (esNotaCreditoProveedor(nroTipo, textoDocumento)) {
      return `/compras/notas-credito/${nroForm}`;
    }

    if (nroTipo === '6' || textoDocumento.toUpperCase().includes('ORDEN DE PAGO')) {
      return `/tesoreria/orden-pago/${nroForm}`;
    }

    return null;
  };

  const getComprobantePath = (item: CuentaCorrienteProveedorComprobante): string | null => {
    const nroFormulario = item.nroFormulario != null && Number(item.nroFormulario) > 0
      ? Number(item.nroFormulario)
      : null;
    const nroTipo = (item.nroTipoFormulario ?? '').toString().trim();
    const comprobanteTxt = (item.comprobante ?? '').toString();
    const numeroTxt = (item.numero ?? '').toString();
    return getComprobantePathFromTipo(nroFormulario, nroTipo, comprobanteTxt, numeroTxt);
  };

  const renderAccionesComprobante = (
    nroForm: number | null,
    nroTipo: string,
    comprobanteTxt: string,
    numero: string,
    comprobantePath: string | null
  ): React.ReactNode => {
    if (!nroForm) return '—';

    const textoDocumento = `${comprobanteTxt} ${numero}`.trim();
    const esCredito = esCreditoPagoProveedor(nroTipo, textoDocumento);
    const esDebitoImp = esDebitoPagoProveedor(nroTipo, textoDocumento);
    const esOrdenPagoPdf = nroTipo === '6' || textoDocumento.toUpperCase().includes('ORDEN');
    const pathDoc =
      comprobantePath ?? getComprobantePathFromTipo(nroForm, nroTipo, comprobanteTxt, numero);
    const sxWallet = {
      bgcolor: '#2e7d32',
      color: '#fff',
      borderRadius: 1.5,
      '&:hover': { bgcolor: '#1b5e20', transform: 'scale(1.1)' },
      transition: 'all 0.2s',
    };
    const sxView = {
      bgcolor: '#0284c7',
      color: '#fff',
      borderRadius: 1.5,
      '&:hover': { bgcolor: '#0369a1', transform: 'scale(1.05)' },
      transition: 'all 0.2s',
    };
    const sxPrint = {
      bgcolor: '#2196f3',
      color: '#fff',
      borderRadius: 1.5,
      '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
      transition: 'all 0.2s',
    };

    const nodes: React.ReactNode[] = [];
    if (esCredito) {
      nodes.push(
        <Tooltip key="w-cred" title="Aplicar saldo / Ver aplicaciones">
          <IconButton
            size="small"
            onClick={() => {
              setNroFormularioCreditoAplicar(nroForm);
              setDebitoIdVerAplicaciones(null);
              setDebitoTipoVerAplicaciones(undefined);
              setCreditoTipoAplicar(nroTipo || undefined);
              const nro = numeroSinPrefijo(numero);
              setNumeroComprobanteCreditoAplicar(nro !== '-' ? nro : undefined);
              setNumeroComprobanteDebitoAplicar(undefined);
              setOpenAplicarSaldoDialog(true);
            }}
            sx={sxWallet}
          >
            <AccountBalanceWalletIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    if (esDebitoImp) {
      nodes.push(
        <Tooltip key="w-deb" title="Ver imputaciones recibidas">
          <IconButton
            size="small"
            onClick={() =>
              abrirVistaDebitoAplicaciones(nroForm, nroTipo, comprobanteTxt, numeroSinPrefijo(numero))
            }
            sx={sxWallet}
          >
            <AccountBalanceWalletIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    if (pathDoc) {
      nodes.push(
        <Tooltip key="view" title="Visualizar documento">
          <IconButton size="small" onClick={() => navigate(pathDoc)} sx={sxView}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    if (esOrdenPagoPdf) {
      nodes.push(
        <Tooltip key="pdf-op" title="Imprimir PDF">
          <IconButton size="small" onClick={() => handleImprimirPDFOrdenPago(nroForm)} sx={sxPrint}>
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    if (nodes.length === 0) return '—';
    return (
      <Stack direction="row" spacing={0.5} justifyContent="center">
        {nodes}
      </Stack>
    );
  };

  // Función auxiliar para cargar todos los comprobantes en lotes de 50 (límite del backend)
  const loadAllComprobantes = useCallback(async (proveedorNum: number): Promise<CuentaCorrienteProveedorComprobante[]> => {
    const maxPageSize = 50; // Límite del backend
    const allComprobantes: CuentaCorrienteProveedorComprobante[] = [];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await cuentaCorrienteProveedoresService.getComprobantesProveedor(proveedorNum, {
        pageNumber: currentPage,
        pageSize: maxPageSize
      });

      allComprobantes.push(...response.items);

      // Si recibimos menos items que el pageSize, no hay más páginas
      if (response.items.length < maxPageSize) {
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return allComprobantes;
  }, []);

  const loadData = useCallback(async (proveedorNum: number) => {
    try {
      setLoading(true);
      
      // Cargar información del proveedor
      const saldosResponse = await cuentaCorrienteProveedoresService.getSaldos({
        pageNumber: 1,
        pageSize: 10000
      });
      
      let proveedorInfoData: CuentaCorrienteProveedorSaldo | null =
        saldosResponse.items.find((item) => item.nroProveedor === proveedorNum) ?? null;

      if (!proveedorInfoData) {
        try {
          const prov = await proveedorService.getProveedor(proveedorNum);
          proveedorInfoData = {
            nroProveedor: prov.nroProveedor,
            nombre: prov.nombre || '',
            saldoTotal: 0,
            saldoVencido: 0,
            saldoVigente: 0,
            ultimaActualizacion: ''
          };
        } catch {
          enqueueSnackbar('Proveedor no encontrado.', { variant: 'error' });
          setProveedorInfo(null);
          setAllComprobantes([]);
          return;
        }
      }

      setProveedorInfo(proveedorInfoData);

      // Cargar todos los comprobantes en lotes de 50 (límite del backend)
      const comprobantes = await loadAllComprobantes(proveedorNum);

      // Ordenar por fecha de emisión (ascendente) y luego por número de comprobante
      const comprobantesOrdenados = [...comprobantes].sort((a, b) => {
        const fechaA = a.fecha ? new Date(a.fecha).getTime() : 0;
        const fechaB = b.fecha ? new Date(b.fecha).getTime() : 0;
        
        // Primero por fecha
        if (fechaA !== fechaB) {
          return fechaA - fechaB;
        }
        
        // Si las fechas son iguales, ordenar por número de comprobante
        const numeroA = a.numero || '';
        const numeroB = b.numero || '';
        return numeroA.localeCompare(numeroB);
      });

      setAllComprobantes(comprobantesOrdenados);
      setPage(0); // Resetear página al cargar nuevo proveedor
    } catch (error) {
      console.error('Error al obtener los datos:', error);
      enqueueSnackbar('No se pudieron cargar los datos.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, loadAllComprobantes]);

  useEffect(() => {
    if (nroProveedor) {
      loadData(Number(nroProveedor));
    }
  }, [nroProveedor, loadData]);

  const loadMovimientosHistoricos = useCallback(async () => {
    if (!nroProveedor || !proveedorInfo) return;
    try {
      setLoadingMovimientos(true);
      const limitedPageSize = Math.min(rowsPerPageMovimientos, 50);
      const response = await cuentaCorrienteProveedoresService.getMovimientosHistoricosProveedor(
        Number(nroProveedor),
        {
          fechaDesde: fechaDesde || undefined,
          fechaHasta: fechaHasta || undefined,
          searchTerm: searchTermMovimientos.trim() || undefined,
          pageNumber: pageMovimientos + 1,
          pageSize: limitedPageSize
        }
      );
      setMovimientosHistoricos(response.items);
      setTotalCountMovimientos(response.totalCount);
      setSaldoInicialPeriodo(response.saldoInicial ?? null);

      let saldoInicial = response.saldoInicial ?? 0;
      if (pageMovimientos > 0) {
        try {
          const totalAnteriores = pageMovimientos * limitedPageSize;
          const maxPageSize = 50;
          for (let offset = 0; offset < totalAnteriores; offset += maxPageSize) {
            const currentPageSize = Math.min(maxPageSize, totalAnteriores - offset);
            const currentPageNumber = Math.floor(offset / maxPageSize) + 1;
            const lote = await cuentaCorrienteProveedoresService.getMovimientosHistoricosProveedor(
              Number(nroProveedor),
              {
                fechaDesde: fechaDesde || undefined,
                fechaHasta: fechaHasta || undefined,
                searchTerm: searchTermMovimientos.trim() || undefined,
                pageNumber: currentPageNumber,
                pageSize: currentPageSize
              }
            );
            for (const item of lote.items) {
              saldoInicial += (item.impCompDebe ?? 0) - (item.impCompHaber ?? 0);
            }
          }
        } catch (err) {
          console.warn('No se pudo calcular el saldo inicial de la página:', err);
        }
      }
      setSaldoInicialPaginaMovimientos(saldoInicial);
    } catch (error) {
      console.error('Error al cargar movimientos históricos:', error);
      enqueueSnackbar('No se pudieron cargar los movimientos históricos.', { variant: 'error' });
      setMovimientosHistoricos([]);
      setTotalCountMovimientos(0);
      setSaldoInicialPaginaMovimientos(0);
      setSaldoInicialPeriodo(null);
    } finally {
      setLoadingMovimientos(false);
    }
  }, [
    nroProveedor,
    proveedorInfo,
    fechaDesde,
    fechaHasta,
    searchTermMovimientos,
    pageMovimientos,
    rowsPerPageMovimientos,
    enqueueSnackbar
  ]);

  const loadMovimientosHistoricosRef = useRef(loadMovimientosHistoricos);
  loadMovimientosHistoricosRef.current = loadMovimientosHistoricos;

  // No incluir filtros (fecha/búsqueda) en deps: se aplican con "Buscar" / Enter.
  // Si no, cada tecla o cambio parcial de fecha dispara el API y muestra errores.
  useEffect(() => {
    if (nroProveedor && proveedorInfo && currentTab === 1) {
      loadMovimientosHistoricosRef.current();
    }
  }, [nroProveedor, proveedorInfo, currentTab, pageMovimientos, rowsPerPageMovimientos]);

  const handleBuscarMovimientos = () => {
    if (pageMovimientos === 0) {
      void loadMovimientosHistoricos();
    } else {
      setPageMovimientos(0);
    }
  };

  const loadAllMovimientosHistoricos = useCallback(async (): Promise<{
    items: MovimientoHistoricoProveedor[];
    saldoInicial: number | null;
  }> => {
    const maxPageSize = 50;
    const allItems: MovimientoHistoricoProveedor[] = [];
    let saldoInicial: number | null = null;
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await cuentaCorrienteProveedoresService.getMovimientosHistoricosProveedor(
        Number(nroProveedor!),
        {
          fechaDesde: fechaDesde || undefined,
          fechaHasta: fechaHasta || undefined,
          searchTerm: searchTermMovimientos.trim() || undefined,
          pageNumber: currentPage,
          pageSize: maxPageSize
        }
      );
      allItems.push(...response.items);
      if (currentPage === 1 && response.saldoInicial !== undefined) {
        saldoInicial = response.saldoInicial;
      }
      if (response.items.length < maxPageSize) hasMore = false;
      else currentPage++;
    }
    return { items: allItems, saldoInicial };
  }, [nroProveedor, fechaDesde, fechaHasta, searchTermMovimientos]);

  const handleProveedorSelect = (proveedor: { nroProveedor: number }) => {
    navigate(`/compras/informes/resumen-cuenta/${proveedor.nroProveedor}`, {
      state: navState ?? undefined,
    });
  };

  const fechaSoloDia = (fecha: string | null | undefined): string => {
    if (!fecha) return '';
    return fecha.length >= 10 ? fecha.slice(0, 10) : fecha;
  };

  const comprobantesFiltrados = allComprobantes.filter((item) => {
    const fechaItem = fechaSoloDia(item.fecha);
    if (!fechaItem) {
      if (fechaDesde || fechaHasta) return false;
    } else {
      if (fechaDesde && fechaItem < fechaDesde) return false;
      if (fechaHasta && fechaItem > fechaHasta) return false;
    }

    const term = searchTermComposicion.trim().toLowerCase();
    if (term) {
      const comprobante = (item.comprobante ?? '').toLowerCase();
      const numero = (item.numero ?? '').toLowerCase();
      if (!comprobante.includes(term) && !numero.includes(term)) return false;
    }
    return true;
  });

  // Igual que movimientos históricos: saldo acumulado anterior a Fecha Desde (viene del backend allí).
  const saldoInicialComposicion = (() => {
    if (!fechaDesde) return null;
    let saldo = 0;
    for (const item of allComprobantes) {
      const fechaItem = fechaSoloDia(item.fecha);
      if (fechaItem && fechaItem < fechaDesde) {
        saldo += item.saldoDeb - item.saldoHab;
      }
    }
    return saldo;
  })();

  // Paginación en el frontend (sobre comprobantes filtrados)
  const totalCount = comprobantesFiltrados.length;
  const maxPageComposicion = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
  const pageComposicion = Math.min(page, maxPageComposicion);
  const startIndex = pageComposicion * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const comprobantes = comprobantesFiltrados.slice(startIndex, endIndex);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePageMovimientos = (_: unknown, newPage: number) => {
    setPageMovimientos(newPage);
  };

  const handleChangeRowsPerPageMovimientos = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    const limited = Math.min(newRowsPerPage, 50);
    setRowsPerPageMovimientos(limited);
    setPageMovimientos(0);
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingExcel(true);

      const data: Record<string, string | number | null | undefined>[] = [];
      let saldoAcumulado = saldoInicialComposicion ?? 0;

      if (fechaDesde && saldoInicialComposicion !== null) {
        data.push({
          Fecha: `Saldo Inicial al ${formatFechaSoloDia(fechaDesde)}`,
          Comprobante: '',
          Número: '',
          Importe: '',
          Debe: '',
          Haber: '',
          'Saldo Acumulado': saldoInicialComposicion,
          'Fecha Vencimiento': '',
          'Nro. Formulario': ''
        });
      }

      for (const item of comprobantesFiltrados) {
        saldoAcumulado += item.saldoDeb - item.saldoHab;
        data.push({
          Fecha: item.fecha ? formatterFecha.format(new Date(item.fecha)) : '',
          Comprobante: item.comprobante,
          Número: numeroSinPrefijo(item.numero) === '-' ? '' : numeroSinPrefijo(item.numero),
          Importe: item.importe,
          Debe: item.saldoDeb,
          Haber: item.saldoHab,
          'Saldo Acumulado': saldoAcumulado,
          'Fecha Vencimiento': item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : '',
          'Nro. Formulario': item.nroFormulario
        });
      }

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ResumenCuenta');

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `resumen_cuenta_proveedor_${nroProveedor}_${today}.xlsx`);
      enqueueSnackbar('Exportación a Excel completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPDF = async () => {
    if (totalCount === 0 || !proveedorInfo) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingPDF(true);

      if (!comprobantesFiltrados.length) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const marginY = 50;
      let currentY = marginY;

      // Título
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Resumen de Cuenta - Cuenta Corriente Proveedores', marginX, currentY);
      currentY += 24;

      // Información del proveedor
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Proveedor: ${nroProveedor} - ${proveedorInfo.nombre}`, marginX, currentY);
      currentY += 20;

      if (fechaDesde || fechaHasta) {
        doc.text(
          `Período: ${fechaDesde ? formatFechaSoloDia(fechaDesde) : '...'} - ${fechaHasta ? formatFechaSoloDia(fechaHasta) : '...'}`,
          marginX,
          currentY
        );
        currentY += 20;
      }

      let saldoAcumulado = saldoInicialComposicion ?? 0;
      const tableData: string[][] = [];

      if (fechaDesde && saldoInicialComposicion !== null) {
        tableData.push([
          `Saldo Inicial al ${formatFechaSoloDia(fechaDesde)}`,
          '',
          '',
          '-',
          '-',
          '-',
          formatCurrency(saldoInicialComposicion),
          ''
        ]);
      }

      for (const item of comprobantesFiltrados) {
        saldoAcumulado += item.saldoDeb - item.saldoHab;
        tableData.push([
          item.fecha ? formatterFecha.format(new Date(item.fecha)) : '',
          item.comprobante || '',
          numeroSinPrefijo(item.numero) === '-' ? '' : numeroSinPrefijo(item.numero),
          formatCurrency(item.importe),
          formatCurrency(item.saldoDeb),
          formatCurrency(item.saldoHab),
          formatCurrency(saldoAcumulado),
          item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : ''
        ]);
      }

      autoTable(doc, {
        startY: currentY,
        head: [['Fecha', 'Comprobante', 'Número', 'Importe', 'Debe', 'Haber', 'Saldo Acumulado', 'Fecha Venc.']],
        body: tableData,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 90 },
          2: { cellWidth: 70 },
          3: { halign: 'right', cellWidth: 70 },
          4: { halign: 'right', cellWidth: 50 },
          5: { halign: 'right', cellWidth: 50 },
          6: { halign: 'right', cellWidth: 75 },
          7: { cellWidth: 70 }
        },
        tableWidth: 'auto'
      });
      currentY = (doc as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? currentY;
      currentY += 16;
      doc.setFont('helvetica', 'bold');
      doc.text(
        `${LABEL_SALDO_CUENTA_CORRIENTE_PERIODO}: ${formatCurrency(calcularSaldoCuentaCorrientePeriodoComposicion())}`,
        marginX,
        currentY
      );

      const pdfBlob = doc.output('blob');
      const today = new Date().toISOString().slice(0, 10);
      const fileName = `resumen_cuenta_proveedor_${nroProveedor}_${today}.pdf`;

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      enqueueSnackbar('PDF descargado correctamente.', { variant: 'success' });
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
    } finally {
      setExportingPDF(false);
    }
  };

  const handleExportExcelMovimientos = async () => {
    if (totalCountMovimientos === 0 || !nroProveedor || !proveedorInfo) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }
    try {
      setExportingExcelMovimientos(true);
      const { items, saldoInicial } = await loadAllMovimientosHistoricos();
      const data: Record<string, string | number>[] = [];
      if (fechaDesde && saldoInicial !== null) {
        data.push({
          'Fecha Emisión': `Saldo Inicial al ${formatFechaSoloDia(fechaDesde)}`,
          'Comprobante': '',
          'Número': '',
          'Descripción': '',
          'Importe Total': '',
          'Debe': '',
          'Haber': '',
          'Saldo Acumulado': saldoInicial,
          'Fecha Venc.': ''
        });
      }
      let saldoAcumulado = saldoInicial ?? 0;
      items.forEach((item) => {
        saldoAcumulado += (item.impCompDebe ?? 0) - (item.impCompHaber ?? 0);
        data.push({
          'Fecha Emisión': item.fechaEmision ? formatterFecha.format(new Date(item.fechaEmision)) : '',
          'Comprobante': item.descripcionTip || '',
          'Número': numeroSinPrefijo(item.numero) === '-' ? '' : numeroSinPrefijo(item.numero),
          'Descripción': item.descripcionFor || '',
          'Importe Total': item.importeTotal ?? 0,
          'Debe': item.impCompDebe ?? 0,
          'Haber': item.impCompHaber ?? 0,
          'Saldo Acumulado': saldoAcumulado,
          'Fecha Venc.': item.fechaVencim ? formatterFecha.format(new Date(item.fechaVencim)) : ''
        });
      });
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'MovimientosHistoricos');
      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `movimientos_historicos_proveedor_${nroProveedor}_${today}.xlsx`);
      enqueueSnackbar('Exportación a Excel completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExportingExcelMovimientos(false);
    }
  };

  const handleExportPDFMovimientos = async () => {
    if (totalCountMovimientos === 0 || !nroProveedor || !proveedorInfo) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }
    try {
      setExportingPDFMovimientos(true);
      const { items, saldoInicial } = await loadAllMovimientosHistoricos();
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const marginX = 28;
      const marginY = 40;
      let currentY = marginY;

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Movimientos Históricos - Cuenta Corriente Proveedores', marginX, currentY);
      currentY += 20;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Proveedor: ${nroProveedor} - ${proveedorInfo.nombre}`, marginX, currentY);
      currentY += 14;
      if (fechaDesde || fechaHasta) {
        doc.text(
          `Período: ${fechaDesde ? formatFechaSoloDia(fechaDesde) : '...'} - ${fechaHasta ? formatFechaSoloDia(fechaHasta) : '...'}`,
          marginX,
          currentY
        );
        currentY += 14;
      }
      currentY += 6;

      const formatMoneyPdf = (amount: number) =>
        formatCurrency(amount).replace(/\u00a0/g, ' ').replace('$ ', '$');

      const tableData: (string | number)[][] = [];
      const anuladoRowIndexes = new Set<number>();
      if (fechaDesde && saldoInicial !== null) {
        tableData.push([
          `Saldo Inicial al ${formatFechaSoloDia(fechaDesde)}`,
          '',
          '',
          '',
          '',
          '',
          '',
          formatMoneyPdf(saldoInicial),
          ''
        ]);
      }
      let saldoAcumulado = saldoInicial ?? 0;
      items.forEach((item) => {
        saldoAcumulado += (item.impCompDebe ?? 0) - (item.impCompHaber ?? 0);
        const tip = (item.descripcionTip || '').trim();
        const comprobante = item.anulado
          ? `[ANULADA] ${tip || 'Comprobante'}`
          : tip;
        if (item.anulado) {
          anuladoRowIndexes.add(tableData.length);
        }
        tableData.push([
          item.fechaEmision ? formatterFecha.format(new Date(item.fechaEmision)) : '',
          comprobante,
          numeroSinPrefijo(item.numero) === '-' ? '' : numeroSinPrefijo(item.numero),
          item.descripcionFor || '',
          formatMoneyPdf(item.importeTotal ?? 0),
          formatMoneyPdf(item.impCompDebe ?? 0),
          formatMoneyPdf(item.impCompHaber ?? 0),
          formatMoneyPdf(saldoAcumulado),
          item.fechaVencim ? formatterFecha.format(new Date(item.fechaVencim)) : ''
        ]);
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const usableWidth = pageWidth - marginX * 2;
      const maxDescLen = items.reduce(
        (max, item) => Math.max(max, (item.descripcionFor || '').trim().length),
        0,
      );
      const descWidth = maxDescLen === 0
        ? 52
        : Math.min(160, Math.max(72, maxDescLen * 4.5));
      const fixedSansDesc =
        58 + 100 + 88 + 72 + 72 + 72 + 78 + 58; // sin columna descripción
      const leftover = Math.max(0, usableWidth - fixedSansDesc - descWidth);
      const moneyExtra = leftover / 4;

      autoTable(doc, {
        startY: currentY,
        head: [['Fecha Emisión', 'Comprobante', 'Número', 'Descripción', 'Importe', 'Debe', 'Haber', 'Saldo Acumulado', 'Fecha Venc.']],
        body: tableData,
        margin: { left: marginX, right: marginX },
        tableWidth: usableWidth,
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak', valign: 'middle' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 8, overflow: 'linebreak' },
        columnStyles: {
          0: { cellWidth: 58 },
          1: { cellWidth: 100 },
          2: { cellWidth: 88, overflow: 'ellipsize' },
          3: { cellWidth: descWidth, overflow: 'linebreak' },
          4: { halign: 'right', cellWidth: 72 + moneyExtra, overflow: 'ellipsize' },
          5: { halign: 'right', cellWidth: 72 + moneyExtra, overflow: 'ellipsize' },
          6: { halign: 'right', cellWidth: 72 + moneyExtra, overflow: 'ellipsize' },
          7: { halign: 'right', cellWidth: 78 + moneyExtra, overflow: 'ellipsize' },
          8: { cellWidth: 58 },
        },
        didParseCell: (data) => {
          if (data.section === 'body' && anuladoRowIndexes.has(data.row.index)) {
            data.cell.styles.fillColor = [230, 230, 230];
            data.cell.styles.textColor = [90, 90, 90];
            data.cell.styles.fontStyle = 'italic';
            if (data.column.index === 1) {
              data.cell.styles.fontStyle = 'bolditalic';
              data.cell.styles.textColor = [66, 66, 66];
            }
          }
        },
      });
      currentY = (doc as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? currentY;
      currentY += 16;
      doc.setFont('helvetica', 'bold');
      doc.text(
        `${LABEL_SALDO_CUENTA_CORRIENTE_PERIODO}: ${formatMoneyPdf(saldoAcumulado)}`,
        marginX,
        currentY
      );

      const pdfBlob = doc.output('blob');
      const today = new Date().toISOString().slice(0, 10);
      const fileName = `movimientos_historicos_proveedor_${nroProveedor}_${today}.pdf`;
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      enqueueSnackbar('PDF descargado correctamente.', { variant: 'success' });
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
    } finally {
      setExportingPDFMovimientos(false);
    }
  };

  // Calcular saldo acumulado para mostrar en la tabla
  const calcularSaldoAcumulado = (indexInPage: number): number => {
    const globalIndex = startIndex + indexInPage;
    let saldo = saldoInicialComposicion ?? 0;
    for (let i = 0; i <= globalIndex; i++) {
      if (comprobantesFiltrados[i]) {
        saldo += comprobantesFiltrados[i].saldoDeb - comprobantesFiltrados[i].saldoHab;
      }
    }
    return saldo;
  };

  const calcularSaldoAcumuladoMovimientos = (indexInPage: number): number => {
    let saldo = saldoInicialPaginaMovimientos;
    for (let i = 0; i <= indexInPage; i++) {
      if (movimientosHistoricos[i]) {
        saldo += (movimientosHistoricos[i].impCompDebe ?? 0) - (movimientosHistoricos[i].impCompHaber ?? 0);
      }
    }
    return saldo;
  };

  const calcularSaldoCuentaCorrientePeriodoComposicion = (): number => {
    let saldo = saldoInicialComposicion ?? 0;
    for (const item of comprobantesFiltrados) {
      saldo += item.saldoDeb - item.saldoHab;
    }
    return saldo;
  };

  const saldoCuentaCorrientePeriodoComposicion = calcularSaldoCuentaCorrientePeriodoComposicion();

  // Saldo inicial: solo en la primera página (página 0) de cada solapa — mismo criterio que movimientos históricos
  const mostrarSaldoInicialComposicion =
    pageComposicion === 0 &&
    Boolean(fechaDesde) &&
    saldoInicialComposicion !== null;
  const mostrarSaldoInicialHistorico =
    Number(pageMovimientos) === 0 &&
    Boolean(fechaDesde) &&
    saldoInicialPeriodo !== null;

  const renderCardCampo = (label: string, valor: React.ReactNode, sx?: object) => (
    <Box sx={sx}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2">{valor}</Typography>
    </Box>
  );

  const renderCardsComposicionMobile = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120} p={3}>
          <CircularProgress />
        </Box>
      );
    }
    if (comprobantes.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120} p={3}>
          <Typography>No hay comprobantes para este proveedor.</Typography>
        </Box>
      );
    }
    return (
      <Stack spacing={2}>
        {mostrarSaldoInicialComposicion && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f0f7ff' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 0.5 }}>
              Saldo Inicial al {formatFechaSoloDia(fechaDesde)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {formatCurrency(saldoInicialComposicion as number)}
            </Typography>
          </Paper>
        )}
        {comprobantes.map((item, index) => {
          const saldoAcumulado = calcularSaldoAcumulado(index);
          const nroForm =
            item.nroFormulario != null && Number(item.nroFormulario) > 0
              ? Number(item.nroFormulario)
              : null;
          const nroTipo = (item.nroTipoFormulario ?? '').toString().trim();
          const comprobanteTxt = (item.comprobante ?? '').toString();
          const comprobantePath = getComprobantePath(item);
          const vencido = Boolean(item.fechaVenc && new Date(item.fechaVenc) < new Date());
          return (
            <Paper key={`${item.numero}-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    {renderCardCampo('Fecha', item.fecha ? formatterFecha.format(new Date(item.fecha)) : '-')}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Comprobante', item.comprobante || '-')}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Número', numeroSinPrefijo(item.numero))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Importe', formatCurrency(item.importe))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Debe', formatCurrency(item.saldoDeb))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Haber', formatCurrency(item.saldoHab))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo(
                      'Saldo Acumulado',
                      <Box component="span" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        {formatCurrency(saldoAcumulado)}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo(
                      'Fecha Venc.',
                      <Box component="span" sx={vencido ? { color: '#d32f2f', fontWeight: 600 } : undefined}>
                        {item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : '-'}
                      </Box>
                    )}
                  </Grid>
                </Grid>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Acciones
                  </Typography>
                  {renderAccionesComprobante(
                    nroForm,
                    nroTipo,
                    comprobanteTxt,
                    item.numero ?? '',
                    comprobantePath
                  )}
                </Box>
              </Stack>
            </Paper>
          );
        })}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'flex-end',
            bgcolor: '#fafafa',
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {LABEL_SALDO_CUENTA_CORRIENTE_PERIODO}:{' '}
            <Box component="span" sx={{ color: '#1976d2' }}>
              {formatCurrency(saldoCuentaCorrientePeriodoComposicion)}
            </Box>
          </Typography>
        </Box>
      </Stack>
    );
  };

  const renderCardsMovimientosMobile = () => {
    if (loadingMovimientos) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120} p={3}>
          <CircularProgress />
        </Box>
      );
    }
    if (movimientosHistoricos.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120} p={3}>
          <Typography>No hay movimientos para este proveedor con los filtros aplicados.</Typography>
        </Box>
      );
    }
    return (
      <Stack spacing={2}>
        {mostrarSaldoInicialHistorico && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f0f7ff' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 0.5 }}>
              Saldo Inicial al {formatFechaSoloDia(fechaDesde)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {formatCurrency(saldoInicialPeriodo as number)}
            </Typography>
          </Paper>
        )}
        {movimientosHistoricos.map((item, index) => {
          const saldoAcumulado = calcularSaldoAcumuladoMovimientos(index);
          const nroForm =
            item.nroFormulario != null && Number(item.nroFormulario) > 0
              ? Number(item.nroFormulario)
              : null;
          const nroTipo = (item.nroTipoFormulario ?? '').toString().trim();
          const descripcionTipTxt = (item.descripcionTip ?? '').toString();
          const comprobantePath = getComprobantePathFromTipo(
            nroForm,
            nroTipo,
            descripcionTipTxt,
            item.numero ?? ''
          );
          const anulado = Boolean(item.anulado);
          const vencido = Boolean(!anulado && item.fechaVencim && new Date(item.fechaVencim) < new Date());
          return (
            <Paper
              key={`${item.numero}-${item.fechaEmision}-${index}`}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: anulado ? 'rgba(0, 0, 0, 0.04)' : undefined,
                opacity: anulado ? 0.65 : 1,
              }}
            >
              <Stack spacing={1.5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    {renderCardCampo(
                      'Fecha Emisión',
                      item.fechaEmision ? formatterFecha.format(new Date(item.fechaEmision)) : '-'
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo(
                      'Comprobante',
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <span>{item.descripcionTip || '-'}</span>
                        {anulado && (
                          <Chip
                            label="Anulada"
                            size="small"
                            sx={{
                              height: 22,
                              fontWeight: 600,
                              bgcolor: '#eeeeee',
                              color: '#616161',
                            }}
                          />
                        )}
                      </Stack>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Número', numeroSinPrefijo(item.numero))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Descripción', item.descripcionFor || '-')}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Importe Total', formatCurrency(item.importeTotal ?? 0))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Debe', formatCurrency(item.impCompDebe ?? 0))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Haber', formatCurrency(item.impCompHaber ?? 0))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo(
                      'Saldo Acumulado',
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 600,
                          color: anulado ? 'text.secondary' : '#1976d2',
                        }}
                      >
                        {formatCurrency(saldoAcumulado)}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo(
                      'Fecha Venc.',
                      <Box component="span" sx={vencido ? { color: '#d32f2f', fontWeight: 600 } : undefined}>
                        {item.fechaVencim ? formatterFecha.format(new Date(item.fechaVencim)) : '-'}
                      </Box>
                    )}
                  </Grid>
                </Grid>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Acciones
                  </Typography>
                  {renderAccionesComprobante(
                    nroForm,
                    nroTipo,
                    descripcionTipTxt,
                    item.numero ?? '',
                    comprobantePath
                  )}
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(backTo)}
              sx={{ textTransform: 'none' }}
            >
              {backLabel}
            </Button>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Resumen de Cuenta Corriente de Proveedores
            </Typography>
          </Stack>

          {/* Formulario de búsqueda */}
          <Card 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              bgcolor: '#fafafa'
            }}
          >
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ProveedorInlineSearchField
                    label="Proveedor"
                    placeholder="Número, nombre o CUIT"
                    displayValue={
                      proveedorInfo
                        ? `${proveedorInfo.nroProveedor} - ${proveedorInfo.nombre}`
                        : nroProveedor ?? ''
                    }
                    onSelect={handleProveedorSelect}
                    onOpenModal={() => setOpenProveedorSearch(true)}
                  />
                </Grid>
                {proveedorInfo && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Nombre"
                        value={proveedorInfo.nombre}
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Saldo Total"
                        value={formatCurrency(proveedorInfo.saldoTotal)}
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& .MuiInputBase-input': {
                              fontWeight: 600,
                              color: '#1976d2',
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Saldo Vencido"
                        value={formatCurrency(proveedorInfo.saldoVencido ?? 0)}
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& .MuiInputBase-input': {
                              fontWeight: 600,
                              color: proveedorInfo.saldoVencido && proveedorInfo.saldoVencido > 0 ? '#d32f2f' : 'inherit',
                            }
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Tabs */}
          {proveedorInfo && (
            <Tabs
              value={currentTab}
              onChange={(_, v: number) => setCurrentTab(v)}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons={isMobile ? 'auto' : false}
              allowScrollButtonsMobile
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Composición de Saldo" />
              <Tab label="Movimientos Históricos" />
            </Tabs>
          )}

          {/* Mensaje cuando no hay proveedor seleccionado */}
          {!proveedorInfo && !loading && (
            <Card 
              sx={{ 
                p: 3, 
                textAlign: 'center',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                bgcolor: '#fafafa'
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Seleccione un proveedor para ver la composición de su saldo
              </Typography>
            </Card>
          )}

          {/* Tab 0: Composición de Saldo */}
          {proveedorInfo && currentTab === 0 && (
            <>
              <Box mb={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<FilterListIcon />}
                    endIcon={showFiltrosComposicion ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowFiltrosComposicion(!showFiltrosComposicion)}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                  >
                    Filtrar
                  </Button>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<AccountBalanceWalletIcon />}
                      onClick={() => {
                        setNroFormularioCreditoAplicar(null);
                        setDebitoIdVerAplicaciones(null);
                        setDebitoTipoVerAplicaciones(undefined);
                        setNumeroComprobanteCreditoAplicar(undefined);
                        setNumeroComprobanteDebitoAplicar(undefined);
                        setCreditoTipoAplicar(undefined);
                        setOpenAplicarSaldoDialog(true);
                      }}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      Aplicar saldos pendientes
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownloadIcon />}
                      onClick={handleExportExcel}
                      disabled={loading || exportingExcel || totalCount === 0}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      {exportingExcel ? 'Exportando...' : 'Exportar Excel'}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={handleExportPDF}
                      disabled={loading || exportingPDF || totalCount === 0}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(245, 158, 11, 0.4)',
                        }
                      }}
                    >
                      {exportingPDF ? 'Generando...' : 'Exportar PDF'}
                    </Button>
                  </Stack>
                </Box>
                <Collapse in={showFiltrosComposicion} timeout="auto" unmountOnExit>
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ mt: 2 }}>
                    <TextField
                      size="small"
                      label="Fecha Desde"
                      type="date"
                      value={fechaDesde}
                      onChange={(e) => { setFechaDesde(e.target.value); setPage(0); setPageMovimientos(0); }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: { xs: '100%', sm: 150 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      size="small"
                      label="Fecha Hasta"
                      type="date"
                      value={fechaHasta}
                      onChange={(e) => { setFechaHasta(e.target.value); setPage(0); setPageMovimientos(0); }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: { xs: '100%', sm: 150 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      size="small"
                      label="Buscar"
                      placeholder="Comprobante, número..."
                      value={searchTermComposicion}
                      onChange={(e) => { setSearchTermComposicion(e.target.value); setPage(0); }}
                      onKeyPress={(e) => { if (e.key === 'Enter') setPage(0); }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ width: { xs: '100%', sm: 220 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={() => setPage(0)}
                      sx={{ borderRadius: 2, textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
                    >
                      Buscar
                    </Button>
                  </Box>
                </Collapse>
              </Box>
              {isMobile ? (
                <Box>
                  {renderCardsComposicionMobile()}
                  {comprobantes.length > 0 && (
                    <TablePagination
                      rowsPerPageOptions={[10, 20, 50, 100]}
                      component="div"
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      page={pageComposicion}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      labelRowsPerPage="Filas por página"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                      sx={{
                        mt: 2,
                        '& .MuiTablePagination-toolbar': {
                          flexWrap: 'wrap',
                          gap: 1,
                          px: 1
                        }
                      }}
                    />
                  )}
                </Box>
              ) : (
              <TableContainer 
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                }}
              >
                {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                  <CircularProgress />
                </Box>
              ) : totalCount === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                  <Typography>
                    {fechaDesde || fechaHasta || searchTermComposicion.trim()
                      ? 'No hay comprobantes para este proveedor con los filtros aplicados.'
                      : 'No hay comprobantes para este proveedor.'}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Número</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Importe</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Debe</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Haber</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Acumulado</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Fecha Venc.</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mostrarSaldoInicialComposicion && (
                        <TableRow
                          key="saldo-inicial-composicion"
                          sx={{ bgcolor: '#f0f7ff', '&:hover': { bgcolor: '#e8f0fe' } }}
                        >
                          <TableCell colSpan={3} sx={{ fontStyle: 'italic' }}>
                            Saldo Inicial al {formatFechaSoloDia(fechaDesde)}
                          </TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                            {formatCurrency(saldoInicialComposicion as number)}
                          </TableCell>
                          <TableCell>-</TableCell>
                          <TableCell align="center">-</TableCell>
                        </TableRow>
                      )}
                      {comprobantes.map((item, index) => {
                        const saldoAcumulado = calcularSaldoAcumulado(index);
                        const nroForm =
                          item.nroFormulario != null && Number(item.nroFormulario) > 0
                            ? Number(item.nroFormulario)
                            : null;
                        const nroTipo = (item.nroTipoFormulario ?? '').toString().trim();
                        const comprobanteTxt = (item.comprobante ?? '').toString();
                        const comprobantePath = getComprobantePath(item);
                        return (
                          <TableRow 
                            key={`${item.numero}-${index}`}
                            sx={{
                              '&:hover': {
                                bgcolor: '#f9f9f9',
                              },
                            }}
                          >
                            <TableCell>
                              {item.fecha ? formatterFecha.format(new Date(item.fecha)) : '-'}
                            </TableCell>
                            <TableCell>{item.comprobante || '-'}</TableCell>
                            <TableCell>{numeroSinPrefijo(item.numero)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.importe)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.saldoDeb)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.saldoHab)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                              {formatCurrency(saldoAcumulado)}
                            </TableCell>
                            <TableCell
                              sx={
                                item.fechaVenc && new Date(item.fechaVenc) < new Date()
                                  ? { color: '#d32f2f', fontWeight: 600 }
                                  : {}
                              }
                            >
                              {item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {renderAccionesComprobante(
                                nroForm,
                                nroTipo,
                                comprobanteTxt,
                                item.numero ?? '',
                                comprobantePath
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={pageComposicion}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Filas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    sx={{
                      borderTop: '1px solid #e0e0e0',
                    }}
                  />
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderTop: '1px solid #e0e0e0',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      bgcolor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {LABEL_SALDO_CUENTA_CORRIENTE_PERIODO}:{' '}
                      <Box component="span" sx={{ color: '#1976d2' }}>
                        {formatCurrency(saldoCuentaCorrientePeriodoComposicion)}
                      </Box>
                    </Typography>
                  </Box>
                </>
              )}
            </TableContainer>
              )}
            </>
          )}

          {/* Tab 1: Movimientos Históricos */}
          {proveedorInfo && currentTab === 1 && (
            <>
              <Box mb={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<FilterListIcon />}
                    endIcon={showFiltrosMovimientos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowFiltrosMovimientos(!showFiltrosMovimientos)}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                  >
                    Filtrar
                  </Button>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownloadIcon />}
                      onClick={handleExportExcelMovimientos}
                      disabled={loadingMovimientos || exportingExcelMovimientos || movimientosHistoricos.length === 0}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      {exportingExcelMovimientos ? 'Exportando...' : 'Exportar Excel'}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={handleExportPDFMovimientos}
                      disabled={loadingMovimientos || exportingPDFMovimientos || movimientosHistoricos.length === 0}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(245, 158, 11, 0.4)'
                        }
                      }}
                    >
                      {exportingPDFMovimientos ? 'Generando...' : 'Exportar PDF'}
                    </Button>
                  </Stack>
                </Box>
                <Collapse in={showFiltrosMovimientos} timeout="auto" unmountOnExit>
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ mt: 2 }}>
                    <TextField
                      size="small"
                      label="Fecha Desde"
                      type="date"
                      value={fechaDesde}
                      onChange={(e) => setFechaDesde(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: { xs: '100%', sm: 150 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      size="small"
                      label="Fecha Hasta"
                      type="date"
                      value={fechaHasta}
                      onChange={(e) => setFechaHasta(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: { xs: '100%', sm: 150 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      size="small"
                      label="Buscar"
                      placeholder="Descripción, comprobante..."
                      value={searchTermMovimientos}
                      onChange={(e) => setSearchTermMovimientos(e.target.value)}
                      onKeyPress={(e) => { if (e.key === 'Enter') handleBuscarMovimientos(); }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ width: { xs: '100%', sm: 220 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={handleBuscarMovimientos}
                      sx={{ borderRadius: 2, textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
                    >
                      Buscar
                    </Button>
                  </Box>
                </Collapse>
              </Box>

              {isMobile ? (
                <Box>
                  {renderCardsMovimientosMobile()}
                  {movimientosHistoricos.length > 0 && (
                    <TablePagination
                      rowsPerPageOptions={[10, 20, 50]}
                      component="div"
                      count={totalCountMovimientos}
                      rowsPerPage={rowsPerPageMovimientos}
                      page={pageMovimientos}
                      onPageChange={handleChangePageMovimientos}
                      onRowsPerPageChange={handleChangeRowsPerPageMovimientos}
                      labelRowsPerPage="Filas por página"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                      sx={{
                        mt: 2,
                        '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 1, px: 1 }
                      }}
                    />
                  )}
                </Box>
              ) : (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0'
                }}
              >
                {loadingMovimientos ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                    <CircularProgress />
                  </Box>
                ) : movimientosHistoricos.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                    <Typography>No hay movimientos para este proveedor con los filtros aplicados.</Typography>
                  </Box>
                ) : (
                  <>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Fecha Emisión</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Número</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Importe Total</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Debe</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Haber</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Acumulado</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Fecha Venc.</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mostrarSaldoInicialHistorico && (
                          <TableRow
                            key="saldo-inicial-historico"
                            sx={{ bgcolor: '#f0f7ff', '&:hover': { bgcolor: '#e8f0fe' } }}
                          >
                            <TableCell colSpan={4} sx={{ fontStyle: 'italic' }}>
                              Saldo Inicial al {formatFechaSoloDia(fechaDesde)}
                            </TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                              {formatCurrency(saldoInicialPeriodo as number)}
                            </TableCell>
                            <TableCell>-</TableCell>
                            <TableCell align="center">-</TableCell>
                          </TableRow>
                        )}
                        {movimientosHistoricos.map((item, index) => {
                          const saldoAcumulado = calcularSaldoAcumuladoMovimientos(index);
                          const nroForm =
                            item.nroFormulario != null && Number(item.nroFormulario) > 0
                              ? Number(item.nroFormulario)
                              : null;
                          const nroTipo = (item.nroTipoFormulario ?? '').toString().trim();
                          const descripcionTipTxt = (item.descripcionTip ?? '').toString();
                          const comprobantePath = getComprobantePathFromTipo(
                            nroForm,
                            nroTipo,
                            descripcionTipTxt,
                            item.numero ?? ''
                          );
                          const anulado = Boolean(item.anulado);
                          return (
                            <TableRow
                              key={`${item.numero}-${item.fechaEmision}-${index}`}
                              sx={{
                                bgcolor: anulado ? 'rgba(0, 0, 0, 0.04)' : undefined,
                                opacity: anulado ? 0.65 : 1,
                                '&:hover': { bgcolor: anulado ? 'rgba(0, 0, 0, 0.06)' : '#f9f9f9' },
                                ...(anulado ? { '& td': { color: 'text.secondary' } } : {}),
                              }}
                            >
                              <TableCell>
                                {item.fechaEmision ? formatterFecha.format(new Date(item.fechaEmision)) : '-'}
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <span>{item.descripcionTip || '-'}</span>
                                  {anulado && (
                                    <Chip
                                      label="Anulada"
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontWeight: 600,
                                        bgcolor: '#eeeeee',
                                        color: '#616161',
                                      }}
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>{numeroSinPrefijo(item.numero)}</TableCell>
                              <TableCell sx={{ maxWidth: 200 }}>{item.descripcionFor || '-'}</TableCell>
                              <TableCell align="right">{formatCurrency(item.importeTotal ?? 0)}</TableCell>
                              <TableCell align="right">{formatCurrency(item.impCompDebe ?? 0)}</TableCell>
                              <TableCell align="right">{formatCurrency(item.impCompHaber ?? 0)}</TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: 600,
                                  color: anulado ? 'text.secondary' : '#1976d2',
                                }}
                              >
                                {formatCurrency(saldoAcumulado)}
                              </TableCell>
                              <TableCell
                                sx={
                                  !anulado && item.fechaVencim && new Date(item.fechaVencim) < new Date()
                                    ? { color: '#d32f2f', fontWeight: 600 }
                                    : {}
                                }
                              >
                                {item.fechaVencim ? formatterFecha.format(new Date(item.fechaVencim)) : '-'}
                              </TableCell>
                              <TableCell align="center">
                                {renderAccionesComprobante(
                                  nroForm,
                                  nroTipo,
                                  descripcionTipTxt,
                                  item.numero ?? '',
                                  comprobantePath
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <TablePagination
                      rowsPerPageOptions={[10, 20, 50]}
                      component="div"
                      count={totalCountMovimientos}
                      rowsPerPage={rowsPerPageMovimientos}
                      page={pageMovimientos}
                      onPageChange={handleChangePageMovimientos}
                      onRowsPerPageChange={handleChangeRowsPerPageMovimientos}
                      labelRowsPerPage="Filas por página"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                      sx={{ borderTop: '1px solid #e0e0e0' }}
                    />
                  </>
                )}
              </TableContainer>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <ProveedorSearch
        open={openProveedorSearch}
        onClose={() => setOpenProveedorSearch(false)}
        onSelect={handleProveedorSelect}
      />

      {pdfUrl && pdfOrdenPagoId != null && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDFOrdenPago}
          title={`Orden de pago ${pdfOrdenPagoId}`}
          documentId={`${pdfOrdenPagoId}`}
          documentType="ordenPago"
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      {proveedorInfo && nroProveedor && (
        <AplicarSaldoReciboDialog
          open={openAplicarSaldoDialog}
          onClose={handleCloseAplicarSaldoDialog}
          variant="proveedor"
          nroCliente={Number(nroProveedor)}
          nombreCliente={proveedorInfo.nombre || `Proveedor ${nroProveedor}`}
          reciboId={debitoIdVerAplicaciones != null ? undefined : nroFormularioCreditoAplicar ?? undefined}
          nroFormularioDebitoCliente={debitoIdVerAplicaciones ?? undefined}
          nroTipoFormularioDebitoCliente={debitoTipoVerAplicaciones}
          numeroComprobanteCredito={numeroComprobanteCreditoAplicar}
          numeroComprobanteDebito={numeroComprobanteDebitoAplicar}
          nroTipoFormularioCredito={creditoTipoAplicar}
          creditosConocidos={creditosConocidosResumen}
          selectorMode={nroFormularioCreditoAplicar == null && debitoIdVerAplicaciones == null}
          onSuccess={() => {
            loadData(Number(nroProveedor));
            if (currentTab === 1) loadMovimientosHistoricos();
          }}
        />
      )}
    </Box>
  );
};

export default ResumenCuenta;



